import React, { useState } from 'react';
import API from '../api';
import { useTranslation } from '../hooks/useTranslation';
import LanguageSwitcher from './LanguageSwitcher';

export default function Login({ onLogin }) {
  const { getMessage } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async () => {
    if (!email || !password) {
      setError('ກະລຸນາໃສ່ອີເມວແລະລະຫັດຜ່ານ');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const res = await API.post('/auth/login', { email, password });
      localStorage.setItem('token', res.data.token);
      onLogin();
    } catch (err) {
      console.error('Login error:', err);
      console.error('Error response:', err.response);
      const errorMessage = err.response?.data?.message || getMessage('login.login_failed');
      setError(`❌ ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen relative overflow-hidden">
      {/* Background - Blue Gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-900 via-blue-700 to-blue-600">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-full h-full" style={{
            backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.4"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v6h6V4z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'
          }}></div>
        </div>
      </div>

      {/* Language Switcher */}
      <div className="absolute top-6 right-6 z-20">
        <LanguageSwitcher compact={true} />
      </div>

      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative z-10 items-center justify-center p-12">
        <div className="text-white text-center">
          {/* PWT Logo */}
          <div className="mb-8 flex justify-center">
            <div className="bg-white rounded-3xl p-6 shadow-2xl">
              <img
                src="/pwt_logo.png"
                alt="PWT Logo"
                className="w-64 h-auto"
              />
            </div>
          </div>

          {/* Bridge Card Image 
          <div className="mb-8 flex justify-center">
            <img 
              src="/pwt_card.jpg" 
              alt="Road Refund Bridge" 
              className="w-full max-w-2xl rounded-2xl shadow-2xl"
            />
          </div>
          */}
          {/* Title */}
          <p className="text-2xl mb-8 opacity-90">
            ລະບົບຄຸ້ມຄອງການເກັບລາຍຮັບທັນສະໄໝກອງທຶນທາງ
          </p>
          <div className="w-32 h-1 bg-white mx-auto mb-8 rounded-full"></div>
          <h1 className="text-5xl font-bold mb-4 drop-shadow-lg">
            ROAD FUND
          </h1>
          <p className="text-lg opacity-80 max-w-md mx-auto">
            Smart Revenue Management System
          </p>

        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 relative z-10">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <div className="inline-flex bg-white rounded-2xl p-3 shadow-xl mb-4">
              <img
                src="/pwt_logo.png"
                alt="PWT Logo"
                className="w-40 h-auto"
              />
            </div>
            <h1 className="text-3xl font-bold text-white drop-shadow-lg mb-2">
              ROAD FUND
            </h1>
            <p className="text-white opacity-90">Smart Toll System</p>
          </div>

          {/* Login Card */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 backdrop-blur-sm">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 dark:bg-blue-900 rounded-full mb-4">
                <span className="text-3xl">🔐</span>
              </div>
              <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">
                {getMessage('login.title')}
              </h2>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                ເຂົ້າສູ່ລະບົບເພື່ອຈັດການ
              </p>
            </div>

            <form onSubmit={(e) => { e.preventDefault(); handleLogin(); }} className="space-y-4">
              {/* Email Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  📧 {getMessage('login.email')}
                </label>
                <input
                  type="email"
                  placeholder={getMessage('login.email_placeholder')}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white transition-all"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                />
              </div>

              {/* Password Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  🔑 {getMessage('login.password')}
                </label>
                <input
                  type="password"
                  placeholder={getMessage('login.password_placeholder')}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white transition-all"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  onKeyPress={e => e.key === 'Enter' && handleLogin()}
                  required
                />
              </div>

              {/* Error Message */}
              {error && (
                <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}

              {/* Login Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white py-3 rounded-lg font-semibold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    ກຳລັງເຂົ້າສູ່ລະບົບ...
                  </span>
                ) : (
                  <span className="flex items-center justify-center">
                    <span className="mr-2">🚀</span>
                    {getMessage('login.login_button')}
                  </span>
                )}
              </button>

              {/* Reset Password Link */}
              <div className="text-center mt-4">
                <a
                  href="/reset-password"
                  className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
                >
                  🔄 ລືມລະຫັດຜ່ານ?
                </a>
              </div>
            </form>

            {/* Footer */}
            <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
              <p className="text-center text-xs text-gray-500 dark:text-gray-400">
                © 2025 Road Refund System. All rights reserved.
              </p>
              <p className="text-center text-xs text-gray-400 dark:text-gray-500 mt-1">
                Powered by OLV Innovation Company Limited
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
